import React, { useMemo, useState, useEffect } from 'react';
import GoogleMapReact from 'google-map-react';
import { Box, Typography, Card, Button, IconButton, Chip } from '@mui/material';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../../../assets/fattyBattyLogo.png';
import { environment } from '../../../environments/environment';

// Simple clustering utility
const createClusters = (stores, zoom) => {
  if (zoom >= 12) {
    // At high zoom levels, show individual markers
    return stores.map((store, index) => ({
      type: 'marker',
      store,
      index,
      lat: (store._geoloc || store.address).lat,
      lng: (store._geoloc || store.address).lng,
    }));
  }

  // Simple grid-based clustering for lower zoom levels
  const gridSize = zoom >= 10 ? 0.01 : zoom >= 8 ? 0.02 : 0.05;
  const clusters = {};

  stores.forEach((store, index) => {
    const location = store._geoloc || store.address;
    if (!location?.lat || !location?.lng) return;

    const gridLat = Math.floor(location.lat / gridSize) * gridSize;
    const gridLng = Math.floor(location.lng / gridSize) * gridSize;
    const key = `${gridLat}_${gridLng}`;

    if (!clusters[key]) {
      clusters[key] = {
        type: 'cluster',
        lat: gridLat + gridSize / 2,
        lng: gridLng + gridSize / 2,
        stores: [],
        indices: [],
      };
    }

    clusters[key].stores.push(store);
    clusters[key].indices.push(index);
  });

  return Object.values(clusters).map((cluster) => {
    if (cluster.stores.length === 1) {
      return {
        type: 'marker',
        store: cluster.stores[0],
        index: cluster.indices[0],
        lat: cluster.lat,
        lng: cluster.lng,
      };
    }
    return cluster;
  });
};

// Cluster marker component
const ClusterMarker = ({ cluster, onClusterClick, lat, lng }) => {
  const count = cluster.stores.length;

  return (
    <Box
      onClick={() => onClusterClick(cluster)}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        transform: 'translate(-50%, -50%)',
        zIndex: 500,
      }}
    >
      <Box
        sx={{
          width: Math.min(50, 30 + count * 2),
          height: Math.min(50, 30 + count * 2),
          backgroundColor: environment.mainColor,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px solid white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translate(-50%, -50%) scale(1.1)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
          },
        }}
      >
        <Typography
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: count > 99 ? '10px' : count > 9 ? '12px' : '14px',
            fontFamily: 'Jost',
          }}
        >
          {count > 99 ? '99+' : count}
        </Typography>
      </Box>
    </Box>
  );
};

// Custom marker component
const BranchMarker = ({
  store,
  index,
  selectedBranch,
  showInfoCard,
  onMarkerClick,
  onFinalizeSelection,
  onClose,
  lat,
  lng,
}) => {
  const isSelected = selectedBranch === index;
  const shouldShowInfoCard = showInfoCard === index;

  const formatAddress = (addr) => {
    if (!addr) return 'Address not available';
    if (typeof addr === 'string') return addr;
    if (addr.displayName && addr.displayName.trim()) return addr.displayName;
    const parts = [];
    if (addr.addressLine1) parts.push(addr.addressLine1);
    if (addr.addressLine2) parts.push(addr.addressLine2);
    const cityStateZip = [addr.city, addr.state, addr.zipCode]
      .filter(Boolean)
      .join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    if (parts.length) return parts.join(' • ');
    // fallback to route/streetNumber
    const routeParts = [];
    if (addr.streetNumber) routeParts.push(addr.streetNumber);
    if (addr.route) routeParts.push(addr.route);
    if (routeParts.length) return routeParts.join(' ');
    return 'Address not available';
  };

  return (
    <Box
      onClick={() => onMarkerClick(index)}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        transform: 'translate(-50%, -100%)',
        zIndex: isSelected ? 1000 : 1,
      }}
    >
      {/* Marker Image (Fatty logo for individual markers) */}
      <Box
        sx={{
          width: isSelected ? 44 : 36,
          height: isSelected ? 44 : 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          overflow: 'hidden',
          border: `3px solid ${isSelected ? environment.mainColor : 'white'}`,
          boxShadow: isSelected
            ? '0 8px 20px rgba(0,0,0,0.22)'
            : '0 4px 12px rgba(0,0,0,0.18)',
          transition: 'all 0.25s ease',
          '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          },
          '&:hover': {
            transform: isSelected ? 'scale(1.02)' : 'scale(1.05)',
          },
        }}
      >
        <img src={logo} alt={store.businessName || store.name || 'Fatty'} />
      </Box>

      {/* Info Card on Click */}
      {shouldShowInfoCard && (
        <Card
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            mb: 1,
            p: 2,
            minWidth: 200,
            maxWidth: 250,
            zIndex: 1001,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            borderRadius: '12px',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid white',
            },
          }}
        >
          {/* Close button to dismiss the info card */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onClose === 'function') onClose();
            }}
            aria-label="Close"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1010,
              background: 'rgba(0,0,0,0.04)',
              '&:hover': { background: 'rgba(0,0,0,0.06)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: '#172B4D',
              fontFamily: 'Jost',
              fontSize: '14px',
              mb: 0.5,
            }}
          >
            {store.businessName || store.name}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#6B7280',
              fontSize: '12px',
              fontFamily: 'Jost',
              mb: 1,
            }}
          >
            {formatAddress(store.address)}
          </Typography>

          {/* Pickup / Delivery badges */}
          {(store.pickup_enabled || store.delivery_enabled) && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              {store.pickup_enabled && (
                <Chip
                  size="small"
                  label="Pickup"
                  sx={{
                    backgroundColor: '#F3F4F6',
                    color: '#111827',
                    fontWeight: 600,
                    fontSize: '11px',
                  }}
                />
              )}

              {store.delivery_enabled && (
                <Chip
                  size="small"
                  label="Delivery"
                  sx={{
           backgroundColor: '#F3F4F6',
                    color: '#111827',
                    fontWeight: 600,
                    fontSize: '11px',
                  }}
                />
              )}
            </Box>
          )}

          {store.distance && (
            <Typography
              variant="caption"
              sx={{
                color: environment.mainColor,
                fontWeight: 600,
                fontSize: '11px',
                fontFamily: 'Jost',
                display: 'block',
                mb: 1,
              }}
            >
              <span role="img" aria-label="location">
                📍
              </span>{' '}
              {store.distance.toFixed(2)} mi away
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
              aria-label={`Order now at ${store.businessName || store.name}`}
              onClick={() => {
                // call the finalize handler for this marker
                if (typeof onFinalizeSelection === 'function')
                  onFinalizeSelection(index);
              }}
              sx={{
                background: `linear-gradient(135deg, ${environment.mainColor} 0%, ${environment.mainColor}CC 100%)`,
                color: '#fff',
                textTransform: 'none',
                borderRadius: '999px',
                px: 2.5,
                py: 0.6,
                fontWeight: 700,
                boxShadow: '0 8px 22px rgba(255,107,53,0.18)',
                '&:hover': {
                  boxShadow: '0 10px 26px rgba(255,107,53,0.22)',
                },
                '&:active': {
                  transform: 'translateY(0px) scale(0.995)',
                },
                transition: 'all 180ms ease',
                minWidth: 110,
              }}
            >
              Order now
            </Button>
          </Box>
        </Card>
      )}
    </Box>
  );
};

// User location marker
const UserLocationMarker = ({ lat, lng }) => (
  <Box
    sx={{
      position: 'relative',
      transform: 'translate(-50%, -50%)',
      zIndex: 999,
    }}
  >
    <Box
      sx={{
        width: 20,
        height: 20,
        backgroundColor: '#4285f4',
        borderRadius: '50%',
        border: '4px solid white',
        boxShadow: '0 4px 8px rgba(66, 133, 244, 0.3)',
        animation: 'pulse 2s infinite',
        '@keyframes pulse': {
          '0%': {
            boxShadow: '0 0 0 0 rgba(66, 133, 244, 0.7)',
          },
          '70%': {
            boxShadow: '0 0 0 10px rgba(66, 133, 244, 0)',
          },
          '100%': {
            boxShadow: '0 0 0 0 rgba(66, 133, 244, 0)',
          },
        },
      }}
    />
  </Box>
);

const BranchMapView = ({
  stores = [],
  userLocation,
  selectedBranch,
  onBranchSelect,
  onFinalizeSelection,
  isLoading = false,
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  // Start zoomed out to show the whole USA by default
  const [zoom, setZoom] = useState(4);
  const [mapApi, setMapApi] = useState(null);
  const [showInfoCard, setShowInfoCard] = useState(null); // Control which marker shows info card

  // Force initial map to show the continental USA centered roughly at
  // the geographic center. This ensures the map opens zoomed out to show
  // the entire country.
  const mapConfig = useMemo(() => {
    return {
      // Center on Milwaukee, WI so the initial view focuses on the city
      center: { lat: 43.0389, lng: -87.9065 }, // Milwaukee, WI
      zoom: 12, // city-level zoom
    };
  }, []);

  // Create clusters based on current zoom level
  const clusteredItems = useMemo(() => {
    const validStores = stores.filter((store) => {
      const location = store._geoloc || store.address;
      return location?.lat && location?.lng;
    });
    return createClusters(validStores, zoom);
  }, [stores, zoom]);

  const handleMarkerClick = (index) => {
    onBranchSelect(index);
    setShowInfoCard(index); // Show info card for this specific marker

    // Pan and zoom the map so the selected marker and its info card are visible.
    try {
      const location = stores[index]?._geoloc || stores[index]?.address;
      if (location && mapApi && mapApi.map) {
        // Center on the selected store
        mapApi.map.panTo({ lat: location.lat, lng: location.lng });

        // Choose a target zoom that reveals the marker details
        const targetZoom = Math.max(12, Math.round(zoom) + 1);
        try {
          mapApi.map.setZoom(targetZoom);
          setZoom(targetZoom);
        } catch (err) {
          // Some map wrappers throw if setZoom called too early; ignore silently
          console.warn('Could not set zoom programmatically', err);
        }
      }
    } catch (err) {
      console.warn('Error while panning/zooming to selected marker', err);
    }
  };

  const handleClusterClick = (cluster) => {
    // If cluster has only a few stores, select the first one
    if (cluster.stores.length <= 3) {
      handleMarkerClick(cluster.indices[0]);
    }
    // For larger clusters, zoom and pan into the cluster center so markers expand
    else if (cluster.stores.length > 3) {
      if (mapApi && mapApi.map) {
        try {
          // Pan to cluster center then increase zoom so cluster splits
          mapApi.map.panTo({ lat: cluster.lat, lng: cluster.lng });
          const targetZoom = Math.min(20, Math.round(zoom) + 2);
          mapApi.map.setZoom(targetZoom);
          setZoom(targetZoom);
        } catch (err) {
          console.warn('Failed to zoom to cluster:', err);
        }
      } else {
        console.log('Map API not ready yet to zoom into cluster');
      }
    }
  };

  const handleMapChange = ({ zoom: newZoom }) => {
    setZoom(newZoom);
  };

  // Hide info card when stores change or component unmounts
  useEffect(() => {
    setShowInfoCard(null);
  }, [stores]);

  if (isLoading) {
    return (
      <Box
        sx={{
          height: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          background: '#f9f9f9',
        }}
      >
        <Typography sx={{ color: '#666' }}>Loading map...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "550px",
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        position: 'relative',
      }}
    >
      <GoogleMapReact
        bootstrapURLKeys={{
          key: environment?.googleMapsApiKey || '',
        }}
        center={mapConfig.center}
        zoom={mapConfig.zoom}
        onChange={handleMapChange}
        onChildClick={(childKey, childProps) => {
          try {
            // Prefer explicit childProps if available
            if (childProps && childProps.cluster) {
              handleClusterClick(childProps.cluster);
              return;
            }

            if (
              childProps &&
              childProps.store &&
              typeof childProps.index === 'number'
            ) {
              handleMarkerClick(childProps.index);
              return;
            }

            // Fallback to parsing the provided key
            if (
              typeof childKey === 'string' &&
              childKey.startsWith('cluster-')
            ) {
              const idx = parseInt(childKey.split('-')[1], 10);
              const item = clusteredItems[idx];
              if (item) handleClusterClick(item);
            } else if (
              typeof childKey === 'string' &&
              childKey.startsWith('marker-')
            ) {
              const idx = parseInt(childKey.split('-')[1], 10);
              const item = clusteredItems.find(
                (it) => it.type === 'marker' && it.index === idx
              );
              if (item) handleMarkerClick(item.index);
            } else if (typeof childKey === 'string') {
              // Try to treat key as index
              const maybeIdx = parseInt(childKey.replace(/[^0-9]/g, ''), 10);
              if (!Number.isNaN(maybeIdx)) handleMarkerClick(maybeIdx);
            }
          } catch (e) {
            console.warn('onChildClick handler error', e);
          }
        }}
        options={{
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }}
        onGoogleApiLoaded={({ map, maps }) => {
          setMapLoaded(true);
          // Do not override the zoom here; keep current zoom state (USA view)
          setMapApi({ map, maps });
         
        }}
        yesIWantToUseGoogleMapApiInternals
      >
        {/* User location marker */}
        {userLocation?.lat && userLocation?.lng && (
          <UserLocationMarker lat={userLocation.lat} lng={userLocation.lng} />
        )}

        {/* Clustered markers */}
        {clusteredItems.map((item, idx) => {
          if (item.type === 'cluster') {
            return (
              <ClusterMarker
                key={`cluster-${idx}`}
                lat={item.lat}
                lng={item.lng}
                cluster={item}
                onClusterClick={handleClusterClick}
              />
            );
          } else {
            return (
              <BranchMarker
                key={item.store.id || `marker-${idx}`}
                lat={item.lat}
                lng={item.lng}
                store={item.store}
                index={item.index}
                selectedBranch={selectedBranch}
                showInfoCard={showInfoCard}
                onMarkerClick={handleMarkerClick}
                onFinalizeSelection={onFinalizeSelection}
                onClose={() => {
                  setShowInfoCard(null); // Hide info card
                  onBranchSelect(null); // Clear selection
                }}
              />
            );
          }
        })}
      </GoogleMapReact>

      {/* Map info overlay */}
      {mapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: '8px',
            p: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '11px',
              fontFamily: 'Jost',
              fontWeight: 500,
            }}
          >
            <span role="img" aria-label="location">
              📍
            </span>{' '}
            {stores.length} location{stores.length !== 1 ? 's' : ''} found
            {zoom < 12 && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: '#999',
                  fontSize: '10px',
                  fontFamily: 'Jost',
                  mt: 0.5,
                }}
              >
                Zoom in to see individual markers
              </Typography>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BranchMapView;
